import { useState } from 'react';
import {
  Siren,
  ShieldCheck,
  AlertTriangle,
  OctagonAlert,
  Thermometer,
  Users,
  MapPin,
  Wind,
  Activity,
  Download,
} from 'lucide-react';
import { isDataUnavailable } from '@/services/dataAvailability';
import { ProductionDataUnavailable } from '@/components/common/ProductionDataUnavailable';
import { useTerritoryStore } from '@/stores/territoryStore';
import { useHeatwave } from '@/hooks/useHeatwave';
import type { HeatwaveData } from '@/hooks/useHeatwave';
import { vigilance, vigilanceBanner } from '@/styles/theme';
import { IndicatorCard } from '@/components/dashboard/IndicatorCard';
import { HeatwaveMap } from './HeatwaveMap';
import type { HeatLayer } from './HeatwaveMap';
import { HeatwaveSimulator } from './HeatwaveSimulator';
import type { SimResult } from './HeatwaveSimulator';

// ── Vigilance icon + label mapping (icône + libellé, jamais couleur seule) ───

const VIGILANCE_ICONS = {
  vert:   ShieldCheck,
  jaune:  AlertTriangle,
  orange: OctagonAlert,
  rouge:  Siren,
} as const;

const VIGILANCE_LABELS = {
  vert:   'Vert — Pas de vigilance particulière',
  jaune:  'Jaune — Vigilance météo',
  orange: 'Orange — Épisode de chaleur',
  rouge:  'Rouge — Canicule',
} as const;

// ── Intégrité des données (badge DEMO) ───────────────────────────────────────
// Le backend renvoie `_demo: true` tant que le payload /heatwave n'est pas
// branché sur une source réelle (Vigilance Météo-France). Cf. finding H3 de
// l'audit du 2026-07-06 : ne jamais présenter une valeur codée en dur comme
// une donnée officielle.

export const DEMO_BADGE_LABEL = 'DONNÉES DE DÉMONSTRATION';

/** `true` si le payload heatwave est une simulation (non branchée sur Météo-France). */
export function isDemoHeatwaveData(data: Pick<HeatwaveData, '_demo'> | null | undefined): boolean {
  return data?._demo === true;
}

/** Libellé de source de vigilance : neutre en mode démo, Météo-France sinon. */
export function getVigilanceSourceLabel(isDemo: boolean): string {
  return isDemo ? 'Vigilance (démonstration)' : 'Vigilance Météo-France';
}

// ── PlanCanicule ─────────────────────────────────────────────────────────────

/**
 * Plan Canicule — 8ᵉ écran Terravision.
 * Aide à la décision en épisode de chaleur : croisement Exposition × Vulnérabilité × Accès.
 * Données issues de l'API (GET /territories/{insee}/heatwave) via useHeatwave.
 */
export function PlanCanicule() {
  const { activeTerritory } = useTerritoryStore();
  const insee = activeTerritory?.code_insee ?? null;
  const { data, isLoading, error, refetch } = useHeatwave(insee);

  const [simResult, setSimResult] = useState<SimResult | null>(null);
  const [activeLayer, setActiveLayer] = useState<HeatLayer>('priority');
  const [showAfter, setShowAfter] = useState(false);

  // ── No territory selected ────────────────────────────────────────────────
  if (!activeTerritory) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <p className="font-sans text-sm text-muted">
          Sélectionnez un territoire pour accéder au Plan Canicule.
        </p>
      </div>
    );
  }


  if (isDataUnavailable(error)) {
    return (
      <ProductionDataUnavailable
        feature="Plan Canicule"
        requiredSources="Vigilance Météo-France, INSEE Filosofi et isochrones piétons"
      />
    );
  }
  // ── Error ─────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <div
          className="rounded-panel flex flex-col gap-2 px-5 py-4 text-sm font-medium"
          style={{ background: '#fdecec', color: '#e1000f' }}
          role="alert"
        >
          <span>Erreur lors du chargement des données canicule.</span>
          <button
            type="button"
            className="self-start font-semibold underline"
            onClick={() => refetch()}
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  // ── Loading ───────────────────────────────────────────────────────────────
  if (isLoading || !data) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <p className="font-sans text-sm text-muted animate-pulse">
          Chargement des données canicule…
        </p>
      </div>
    );
  }

  // ── Derived KPI values (reactive to simulation) ───────────────────────────
  const coveragePct  = simResult?.coverage_after ?? data.coverage_pct;
  const desertsCount = simResult
    ? Math.max(0, data.cool_deserts - simResult.deserts_closed)
    : data.cool_deserts;
  const refugesCount = simResult
    ? data.refuges_count + (simResult.refuges_added ?? 0)
    : data.refuges_count;

  const vig     = vigilance[data.vigilance];
  const VigIcon = VIGILANCE_ICONS[data.vigilance];
  const isRouge = data.vigilance === 'rouge';
  const isDemo  = isDemoHeatwaveData(data);

  return (
    <div className="flex flex-col gap-4 p-5">

      {/* ── Bandeau de vigilance ──────────────────────────────────────────── */}
      <div
        className="flex items-center gap-4 rounded-panel px-5 py-3.5"
        style={{ background: isRouge ? vigilanceBanner : vig.bg }}
        role="region"
        aria-label={`Vigilance canicule : ${VIGILANCE_LABELS[data.vigilance]}`}
      >
        {/* Icône + libellé — jamais la couleur seule (accessibilité) */}
        <VigIcon
          size={22}
          aria-hidden="true"
          style={{ color: isRouge ? '#fff' : vig.color }}
        />
        <span
          className="font-display text-[15px] font-extrabold"
          style={{ color: isRouge ? '#fff' : vig.color }}
        >
          {VIGILANCE_LABELS[data.vigilance]}
        </span>

        {/* Infos épisode */}
        <span
          className="text-[13px] font-medium"
          style={{ color: isRouge ? 'rgba(255,255,255,.85)' : vig.color }}
        >
          Épisode J{data.day}/{data.total_days}
          {data.next_wave_in > 0 && ` · Prochaine vague J+${data.next_wave_in}`}
        </span>

        {/* Badge DEMO — donnée de vigilance simulée, non branchée sur Météo-France */}
        {isDemo && (
          <span
            className="rounded-full px-3 py-1 text-[11.5px] font-bold"
            style={{ background: '#fff4e5', color: '#ef7d18' }}
            role="status"
            title="Vigilance simulée — non connectée à Météo-France"
          >
            {DEMO_BADGE_LABEL}
          </span>
        )}

        {/* Bouton export */}
        <button
          type="button"
          title="Bientôt disponible"
          className="ml-auto flex items-center gap-1.5 rounded-[9px] border px-3 py-1.5 text-[12.5px] font-semibold transition hover:opacity-80"
          style={
            isRouge
              ? { color: '#fff', borderColor: 'rgba(255,255,255,.5)', background: 'rgba(255,255,255,.15)' }
              : { color: vig.color, borderColor: vig.color, background: 'transparent' }
          }
          aria-label="Exporter la note de crise"
        >
          <Download size={14} aria-hidden="true" />
          Exporter la note de crise
        </button>
      </div>

      {/* ── Rangée KPI (5 cards) ─────────────────────────────────────────── */}
      <div className="grid grid-cols-5 gap-3">
        <IndicatorCard
          label="Température max"
          value={data.temp_max ?? 39}
          unit="°C"
          icon={<Thermometer size={15} />}
          color="#e1000f"
        />
        <IndicatorCard
          label="Population vulnérable"
          value={data.pop_vulnerable}
          unit="hab."
          icon={<Users size={15} />}
          color="#e1000f"
        />
        <IndicatorCard
          label="Refuges actifs"
          value={refugesCount}
          icon={<MapPin size={15} />}
          trend={simResult && simResult.refuges_added > 0 ? 'up' : 'stable'}
          color="#1f8a5b"
        />
        <IndicatorCard
          label="Déserts de fraîcheur"
          value={desertsCount}
          icon={<Wind size={15} />}
          trend={simResult && simResult.deserts_closed > 0 ? 'down' : 'stable'}
          color={desertsCount > 0 ? '#ef7d18' : '#1f8a5b'}
        />
        <IndicatorCard
          label="Couverture < 10 min"
          value={coveragePct}
          unit="%"
          icon={<Activity size={15} />}
          trend={simResult ? 'up' : 'stable'}
          color="#1f8a5b"
        />
      </div>

      {/* ── Grille 1.55fr / 1fr : HeatwaveMap | HeatwaveSimulator ────────── */}
      <div className="grid gap-4" style={{ gridTemplateColumns: '1.55fr 1fr', minHeight: '520px' }}>
        <HeatwaveMap
          layer={activeLayer}
          onLayerChange={setActiveLayer}
          showAfter={showAfter}
          onShowAfterChange={setShowAfter}
          simDone={simResult !== null}
        />
        <HeatwaveSimulator
          coverBaseline={data.coverage_pct}
          popVulnerable={data.pop_vulnerable}
          onResult={setSimResult}
        />
      </div>

      {/* ── Bas : Zones prioritaires | Alertes équipements sensibles ─────── */}
      <div className="grid gap-4" style={{ gridTemplateColumns: '1.55fr 1fr' }}>

        {/* Zones prioritaires */}
        <div className="rounded-panel border border-line bg-white shadow-card overflow-hidden">
          <div className="border-b border-line px-4 py-3">
            <h3 className="font-display text-[14px] font-bold text-ink">Zones prioritaires</h3>
            <p className="text-[11.5px] text-faint">
              Classées par indice Exposition × Vulnérabilité × Déficit d&apos;accès
            </p>
          </div>
          {data.zones.length === 0 ? (
            <p className="p-4 text-sm text-muted">Aucune zone à afficher.</p>
          ) : (
            <table className="w-full text-[12.5px]" aria-label="Zones prioritaires canicule">
              <thead className="bg-surface-subtle">
                <tr>
                  <th scope="col" className="px-4 py-2 text-left font-semibold text-faint">Rang</th>
                  <th scope="col" className="px-4 py-2 text-left font-semibold text-faint">Secteur</th>
                  <th scope="col" className="px-4 py-2 text-right font-semibold text-faint">Indice</th>
                  <th scope="col" className="px-4 py-2 text-right font-semibold text-faint">Pop. vuln.</th>
                  <th scope="col" className="px-4 py-2 text-right font-semibold text-faint">Refuge (min)</th>
                </tr>
              </thead>
              <tbody>
                {data.zones.map((z) => (
                  <tr key={z.rank} className="border-t border-line hover:bg-surface-subtle transition-colors">
                    <td className="px-4 py-2.5">
                      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#e1000f] font-mono text-[10.5px] font-bold text-white">
                        {z.rank}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 font-medium text-ink">{z.sector}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-ink">
                      {Math.round(z.priority * 100)}/100
                    </td>
                    <td className="px-4 py-2.5 text-right text-muted">
                      {z.pop_vulnerable.toLocaleString('fr-FR')}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <span
                        className="font-mono font-semibold"
                        style={{ color: z.nearest_refuge_min > 10 ? '#e1000f' : '#1f8a5b' }}
                      >
                        {z.nearest_refuge_min} min
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Alertes équipements sensibles */}
        <div className="rounded-panel border border-line bg-white shadow-card overflow-hidden">
          <div className="border-b border-line px-4 py-3">
            <h3 className="font-display text-[14px] font-bold text-ink">
              Alertes équipements sensibles
            </h3>
            <p className="text-[11.5px] text-faint">EHPAD, crèches, écoles en zone critique</p>
          </div>
          {data.sensitive.length === 0 ? (
            <p className="p-4 text-sm text-muted">Aucune alerte.</p>
          ) : (
            <ul className="divide-y divide-line" aria-label="Alertes équipements sensibles">
              {data.sensitive.map((s, idx) => (
                <li
                  key={idx}
                  className="flex items-start gap-3 px-4 py-3 hover:bg-surface-subtle transition-colors"
                >
                  {/* Carré = équipement sensible (forme ≠ couleur, accessibilité) */}
                  <span
                    aria-hidden="true"
                    className="mt-0.5 h-3 w-3 flex-shrink-0 rounded-[2px]"
                    style={{ background: s.critical ? '#e1000f' : '#e08600' }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-ink text-[13px] truncate">{s.name}</p>
                    <p className="text-[11.5px] text-faint capitalize">
                      {s.kind} · {s.access_min} min au refuge le plus proche
                    </p>
                  </div>
                  {s.critical && (
                    <span
                      className="flex-shrink-0 rounded-full px-2 py-0.5 text-[10.5px] font-bold"
                      style={{ background: '#fdecec', color: '#e1000f' }}
                      role="status"
                      aria-label="Zone critique"
                    >
                      Critique
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Sources de données */}
      <p className="pb-1 text-[10.5px] text-faint">
        Sources : INSEE (carroyage pop./âge), BDNB (DPE), BDTOPO (bâti/équipements),
        OSM/osmnx (réseau piéton), {getVigilanceSourceLabel(isDemo)}.
        Cadre : registre nominatif CCAS (loi 2004), plan ORSEC.
      </p>
    </div>
  );
}
